import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

let latestQuickAddRequest = 0;

/**
 * v2 quick-add panels (sections/xz-v2-quick-add.liquid), cached per section URL for the page's life
 * like the legacy branch's #cachedContent, but shared across cards (PDP upsell + PLP grid can point
 * at the same product) and filled by hover/focus prefetch. Entries hold a pristine element; callers clone.
 * @type {Map<string, {at: number, promise: Promise<Element | null>}>}
 */
const quickAddSections = new Map();
const QUICK_ADD_CACHE_MS = 5 * 60 * 1000;

/** @param {string} sectionUrl @returns {Promise<Element | null>} */
function loadQuickAddSection(sectionUrl) {
  const hit = quickAddSections.get(sectionUrl);
  if (hit && Date.now() - hit.at < QUICK_ADD_CACHE_MS) return hit.promise;
  const promise = fetch(sectionUrl)
    .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('[data-xzc-quick-add]'))
    .then((panel) => {
      if (!panel) quickAddSections.delete(sectionUrl);
      return panel;
    })
    .catch(() => {
      quickAddSections.delete(sectionUrl);
      return null;
    });
  quickAddSections.set(sectionUrl, { at: Date.now(), promise });
  return promise;
}

/** @param {string} productUrl */
function quickAddSectionUrl(productUrl) {
  const url = new URL(productUrl);
  url.searchParams.set('section_id', 'xz-v2-quick-add');
  return url.toString();
}

/** Extract native accessory controls before connection, never mounting another engraving owner. */
function accessoryQuickAddContent(html, title, url) {
  const source = html.querySelector('.xz-v2-pdp-details');
  const form = source?.querySelector('product-form-component');
  const picker = source?.querySelector('variant-picker');
  const price = source?.querySelector('product-price');
  if (!form || !picker || !price) return null;
  const content = document.createElement('div');
  content.dataset.v2AccessoryDialog = '';
  const heading = document.createElement('h2');
  const link = document.createElement('a');
  link.href = url; link.textContent = title; heading.append(link);
  form.querySelectorAll('engraving-option-component, .sheath-upsell, [ref="acceleratedCheckoutButtonContainer"], script:not([type="application/json"])').forEach(node => node.remove());
  picker.querySelectorAll('[onclick]').forEach(node => node.removeAttribute('onclick'));
  picker.dataset.templateProductMatch = 'false';
  form.dataset.v2AccessoryForm = 'true';
  content.append(heading, price, picker, form);
  return content;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  #requestGeneration = 0;
  #dialogCleanup;

  get productPageUrl() {
    if (this.dataset.accessoryUrl) return new URL(this.dataset.accessoryUrl, window.location.origin).toString();
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  #prefetchTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    if (this.dataset.accessoryUrl) {
      this.addEventListener('pointerenter', this.#onPointerEnter);
      this.addEventListener('pointerleave', this.#cancelPrefetch);
      this.addEventListener('focusin', this.#prefetch);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#cancelPrefetch);
    this.removeEventListener('focusin', this.#prefetch);
    this.#cancelPrefetch();
    this.#abortController?.abort();
    this.#requestGeneration++;
    this.#setBusy(false);
    this.#dialogCleanup?.();
  }

  /** Mouse hover intent (not touch: a tap fires the click straight after) warms the panel cache. */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#cancelPrefetch();
    this.#prefetchTimer = window.setTimeout(this.#prefetch, 150);
  };

  #cancelPrefetch = () => {
    window.clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = 0;
  };

  #prefetch = () => {
    this.#cancelPrefetch();
    const url = this.productPageUrl;
    if (url) loadQuickAddSection(quickAddSectionUrl(url));
  };

  /** The trigger (card cart icon / upsell ADD) shows progress while the panel loads. */
  #busyTrigger = null;

  /** @param {boolean} busy @param {Element | null} [trigger] */
  #setBusy(busy, trigger) {
    if (busy) {
      this.#busyTrigger = trigger || this.querySelector('a, button');
      this.#busyTrigger?.setAttribute('aria-busy', 'true');
      this.#busyTrigger?.classList.add('xzc-qa-trigger--busy');
      return;
    }
    this.#busyTrigger?.removeAttribute('aria-busy');
    this.#busyTrigger?.classList.remove('xzc-qa-trigger--busy');
    this.#busyTrigger = null;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    // A second tap while this card's panel is still loading is ignored (it would restart the fetch).
    if (this.#busyTrigger) return;

    const currentUrl = this.productPageUrl;
    const generation = ++this.#requestGeneration;
    const request = ++latestQuickAddRequest;
    const current = () => this.isConnected && generation === this.#requestGeneration && request === latestQuickAddRequest;
    const fallback = () => { if (current() && currentUrl) window.location.assign(currentUrl); };
    const dialog = document.getElementById('quick-add-dialog');
    if (!currentUrl || !(dialog instanceof QuickAddDialog)) { fallback(); return; }
    // component.js delegates on:click from document and proxies event.target to the [on:click] element.
    const trigger = event.target instanceof Element && event.target !== this && this.contains(event.target) ? event.target : null;
    this.#setBusy(true, trigger);
    try {
      let productGrid;
      if (this.dataset.accessoryUrl) {
        // v2: the dedicated quick-add section (sections/xz-v2-quick-add.liquid) rendered for this
        // product through the Section Rendering API (cached per URL); the legacy PDP extraction is the fallback.
        const panel = await loadQuickAddSection(quickAddSectionUrl(currentUrl));
        if (!current()) return;
        productGrid = panel ? /** @type {Element} */ (panel.cloneNode(true)) : null;
        if (!productGrid) {
          const html = await this.fetchProductPage(currentUrl);
          if (!current()) return;
          productGrid = html && accessoryQuickAddContent(html, this.dataset.productTitle || '', currentUrl);
        }
      } else {
        productGrid = this.#cachedContent.get(currentUrl);
        if (!productGrid) {
          const html = await this.fetchProductPage(currentUrl);
          if (!current()) return;
          const grid = html?.querySelector('[data-product-grid-content]');
          if (grid) { productGrid = grid.cloneNode(true); this.#cachedContent.set(currentUrl, productGrid); }
        }
        productGrid = productGrid?.cloneNode(true);
      }
      if (!current()) return;
      if (!productGrid) { fallback(); return; }
      const updated = await this.updateQuickAddModal(productGrid);
      if (!current()) return;
      if (!updated) { fallback(); return; }
      this.#openQuickAddModal();
    } catch (error) {
      if (error?.name !== 'AbortError') fallback();
    } finally {
      if (generation === this.#requestGeneration) this.#setBusy(false);
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.#dialogCleanup?.();
    this.toggleAttribute('stay-visible', true);
    const clear = () => this.toggleAttribute('stay-visible', false);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, clear, { once: true });
    this.#dialogCleanup = () => { dialogComponent.removeEventListener(DialogCloseEvent.eventName, clear); clear(); };
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    // The v2 panel (sections/xz-v2-quick-add.liquid) is one markup for every width; its CSS
    // re-lays out the open dialog, so crossing Horizon's 750px breakpoint must not dismiss it.
    if (document.querySelector('#quick-add-modal-content [data-xzc-quick-add]')) return;
    this.#requestGeneration++;
    this.#abortController?.abort();
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    if (productGrid.hasAttribute('data-v2-accessory-dialog') || productGrid.hasAttribute('data-xzc-quick-add')) {
      modalContent.replaceChildren(productGrid);
      return true;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return false;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return false;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    return true;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    this.addEventListener('click', this.#accessoryVariantClick, { capture: true, signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  #accessoryVariantClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-v2-accessory-dialog]')) return;
    // A variant morph can restore legacy inline handlers; native change owns this dialog.
    target.closest('variant-picker')?.querySelectorAll('[onclick]').forEach(node => node.removeAttribute('onclick'));
    const anchor = target.closest('a.product-variant');
    const input = anchor?.closest('label')?.querySelector('input[type="radio"]');
    if (!input) return;
    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
